import React, { useState, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { FileText, Printer, ChevronDown, ChevronUp, Monitor, PenTool, Send, BarChart3, Mail, Settings, Lightbulb, Shield, Image } from 'lucide-react';

const UserGuide = () => {
  const [expandedSections, setExpandedSections] = useState(() => {
    const initial = {};
    sections.forEach(s => { initial[s.id] = true; });
    return initial;
  });
  const [searchQuery, setSearchQuery] = useState('');
  const printRef = useRef();

  const toggleSection = (id) => {
    setExpandedSections(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const toggleAll = (expand) => {
    const newState = {};
    sections.forEach(s => { newState[s.id] = expand; });
    setExpandedSections(newState);
  };

  const filteredSections = searchQuery
    ? sections.filter(s =>
        s.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        s.content.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : sections;

  const handlePrint = () => {
    toggleAll(true);
    setTimeout(() => window.print(), 300);
  };

  return (
    <div className="min-h-screen p-6" data-testid="user-guide-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1
            className="text-2xl md:text-3xl font-bold tracking-wider uppercase mb-1"
            style={{
              fontFamily: "'Orbitron', monospace",
              color: '#00e5ff',
              textShadow: '0 0 20px rgba(0,229,255,0.3)'
            }}
          >
            MODE_D'EMPLOI
          </h1>
          <p className="text-slate-500 font-mono text-sm">
            Guide complet d'utilisation d'Auto Edito v3.8
          </p>
        </div>

        <div className="flex gap-2">
          <Button
            onClick={() => toggleAll(true)}
            variant="outline"
            size="sm"
            className="border-slate-600 text-slate-300"
            data-testid="expand-all-btn"
          >
            Tout ouvrir
          </Button>
          <Button
            onClick={() => toggleAll(false)}
            variant="outline"
            size="sm"
            className="border-slate-600 text-slate-300"
            data-testid="collapse-all-btn"
          >
            Tout fermer
          </Button>
          <Button
            onClick={handlePrint}
            className="bg-sky-600 hover:bg-sky-500"
            size="sm"
            data-testid="print-guide-btn"
          >
            <Printer className="w-4 h-4 mr-2" />
            PDF
          </Button>
        </div>
      </div>

      {/* Search */}
      <div className="max-w-4xl mx-auto mb-6">
        <input
          type="text"
          placeholder="Rechercher dans le guide..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full bg-slate-900/50 border border-slate-700 rounded-lg px-4 py-2 text-slate-300 font-mono text-sm focus:border-sky-500 focus:outline-none"
          data-testid="guide-search"
        />
      </div>

      {/* Guide Content */}
      <div ref={printRef} className="max-w-4xl mx-auto space-y-3">
        {filteredSections.map((section) => (
          <Card
            key={section.id}
            className="bg-slate-900/50 border-slate-700"
            data-testid={`section-${section.id}`}
          >
            <CardHeader
              className="cursor-pointer hover:bg-slate-800/50 transition-colors py-3"
              onClick={() => toggleSection(section.id)}
            >
              <CardTitle className="flex items-center justify-between text-sky-300 text-base">
                <span className="flex items-center gap-3">
                  <section.icon className="w-5 h-5 text-sky-500" />
                  {section.title}
                </span>
                {expandedSections[section.id] ? (
                  <ChevronUp className="w-5 h-5" />
                ) : (
                  <ChevronDown className="w-5 h-5" />
                )}
              </CardTitle>
            </CardHeader>
            {expandedSections[section.id] && (
              <CardContent className="text-slate-300 whitespace-pre-line font-mono text-sm leading-relaxed pt-0 pb-4">
                {section.content}
              </CardContent>
            )}
          </Card>
        ))}

        {filteredSections.length === 0 && (
          <p className="text-center text-slate-500 font-mono py-8">
            Aucun resultat pour "{searchQuery}"
          </p>
        )}
      </div>

      {/* Footer */}
      <div className="mt-10 text-center text-slate-500 text-sm font-mono">
        <p>Auto Edito v3.8.0 - Jean-Baptiste Giraud & Paolo Garoscio - LPP Logiciel</p>
      </div>

      {/* Print styles */}
      <style>{`
        @media print {
          body { background: white !important; color: black !important; }
          nav, button, input, footer { display: none !important; }
          .bg-slate-900\\/50, .bg-slate-950 { background: white !important; border: 1px solid #ddd !important; }
          .text-sky-300, .text-sky-400, .text-sky-500, .text-slate-300 { color: #1a1a1a !important; }
          .text-slate-500 { color: #666 !important; }
          .border-slate-700 { border-color: #ddd !important; }
          * { text-shadow: none !important; }
        }
      `}</style>
    </div>
  );
};

// Sections defined outside component for stable reference
const sections = [
  {
    id: 'intro',
    title: '1. Presentation',
    icon: Monitor,
    content: `Auto Edito est votre assistant de redaction et publication assistee par intelligence artificielle.

Fonctionnalites principales :
  - Generation d'articles complets a partir d'un sujet ou d'une dictee vocale
  - Publication directe sur vos sites WordPress (brouillon ou en ligne)
  - Optimisation SEO automatique (score minimum de 60 requis pour publier)
  - 3 options d'images : IA (comics 60s), Shutterstock/Periscope (3 choix), ou aucune
  - Titre de l'auteur respecte a la lettre si fourni
  - Mode verbose : suivi detaille etape par etape de la generation
  - Recherche web automatique pour enrichir avec des donnees et chiffres
  - Protection anti-doublons : empeche la publication de 2 articles differents avec le meme titre
  - Suivi des couts de generation
  - Traitement automatique des tribunes recues par email (extraction LLM)
  - Outils de surveillance : Radar (RSS), Sonar (pages web), Cible (publications)
  - Recherche d'images : Periscope (Wikimedia, Pixabay, Pexels, Flickr, Shutterstock)`
  },
  {
    id: 'connexion',
    title: '2. Connexion',
    icon: Shield,
    content: `Pour vous connecter :
  1. Ouvrez Auto Edito dans votre navigateur
  2. Entrez votre adresse email et votre mot de passe
  3. Une sequence de demarrage s'affiche (style "cockpit")
  4. Vous accedez ensuite au Centre de Commande

Roles utilisateurs :
  - AMIRAL : Acces total a toutes les fonctionnalites et tous les sites
  - CAPITAINE : Acces intermediaire, sites restreints
  - LIEUTENANT : Acces limite aux sites autorises uniquement

Votre session reste active 30 jours. Si vous perdez votre mot de passe, contactez l'administrateur.`
  },
  {
    id: 'dashboard',
    title: '3. Centre de Commande',
    icon: Monitor,
    content: `Le Dashboard affiche tous les sites sous forme de boutons "cockpit" :

  BLANC clignotant = Site actif et accessible
  ROUGE clignotant = Site non configure ou acces non autorise
  Grise avec cadenas = Sites humoristiques verrouilles
  Badge "EXT" = Site externe

Le bouton "MULTI SITES SPECIAL" genere un article pour plusieurs sites simultanement, chacun avec son angle editorial.

Cliquez sur un site pour commencer a creer un article.`
  },
  {
    id: 'creation',
    title: '4. Creation d\'article',
    icon: PenTool,
    content: `Pour creer un article :

  1. Cliquez sur un site ou sur "MULTI SITES SPECIAL"
  2. Choisissez votre mode de saisie :
     - Sujet : Dictez ou tapez votre sujet librement
     - Texte a reformuler : Collez un texte existant
     - Document : Uploadez un fichier (Word, PDF, TXT)

  3. Options supplementaires :
     - URL de reference : Ajoutez des liens web (contenu extrait automatiquement)
     - Notes additionnelles : Precisions pour l'IA
     - Prompt personnalise par site : Instructions specifiques de redaction

  4. Titre de l'article (NOUVEAU v3.8) :
     - Si vous imposez un titre, il sera respecte A LA LETTRE par l'IA
     - Si vous laissez vide, l'IA choisira un titre optimise SEO

  5. Options d'image (NOUVEAU v3.8) :
     - Pas d'image : Aucune image generee
     - Image IA : Style comics annees 60 (genere par Gemini, +0.02 EUR)
     - Shutterstock/Periscope : 3 images de banques d'images (gratuit, choix parmi 3)

  6. Type de contenu :
     - Article classique / Tribune-Avis d'expert / Editorial

  7. Cliquez sur "Generer l'article" et suivez la progression detaillee

  RECHERCHE WEB AUTOMATIQUE (NOUVEAU v3.8) :
  Si votre sujet ou vos notes contiennent des mots comme "enrichir", "donnees", 
  "chiffres", "statistiques", le systeme effectue automatiquement une recherche web
  et injecte les resultats dans le contexte de l'IA.`
  },
  {
    id: 'generation',
    title: '5. Generation & Resultats',
    icon: BarChart3,
    content: `Pendant la generation :
  - Une barre de progression indique l'avancement par site
  - Le JOURNAL DETAILLE (NOUVEAU v3.8) affiche chaque etape en temps reel :
    - Demarrage de la generation
    - Recherche web (si demandee)
    - Redaction par le LLM
    - Score SEO calcule
    - Generation/recherche de l'image
    - Completion

Resultat genere automatiquement :
  - Titre (respecte si impose, sinon optimise SEO)
  - Contenu de 900+ mots avec intertitres H2/H3
  - Chapo/resume contenant le mot-cle principal
  - Mots-cles et categories suggerees
  - Titre SEO et meta description optimises
  - Chiffre cle avec legende (pour les champs ACF)
  - Image a la une (IA ou Shutterstock selon choix)
  - Mot-cle focus pour Rank Math
  - SCORE SEO affiche (NOUVEAU v3.8) : vert (>60), jaune (40-60), rouge (<40)

EDITION AVANT PUBLICATION (NOUVEAU v3.8) :
  - Le titre, resume et contenu sont editables directement dans le formulaire
  - Bouton "Sauvegarder" pour enregistrer les modifications
  - Bouton "Edition avancee" pour acceder a tous les champs SEO

Cout moyen : ~0.05 EUR par article (texte + image IA)`
  },
  {
    id: 'publication',
    title: '6. Publication sur WordPress',
    icon: Send,
    content: `Apres generation :

  1. Verifiez le contenu (editable directement)
  2. Choisissez le statut : Brouillon ou Publie
  3. Cliquez sur "Publier"

  CONTROLE SEO (NOUVEAU v3.8) :
  La publication est BLOQUEE si le score SEO est inferieur a 60.
  Ameliorez le titre, le focus keyword et la meta description avant de publier.

  PROTECTION ANTI-DOUBLONS (NOUVEAU v3.8) :
  Le systeme empeche de publier un AUTRE article avec le meme titre sur le meme site.
  Republier le MEME article (mise a jour brouillon vers publie) est autorise.

L'article est cree sur WordPress avec :
  - Image a la une uploadee automatiquement
  - Categories et tags assignes
  - SEO configure : titre, meta description, mot-cle focus (Rank Math ou Yoast)
  - Champs ACF remplis (chiffre + legende)
  - Auteur determine automatiquement

  LIEN ARTICLE EN LIGNE (NOUVEAU v3.8) :
  Apres publication, le lien vers l'article est affiche de maniere visible.
  Cliquez dessus pour voir l'article publie directement.`
  },
  {
    id: 'tribunes',
    title: '7. Tribunes par Email',
    icon: Mail,
    content: `Auto Edito traite automatiquement les tribunes recues par email :

  1. L'auteur envoie un email a tribunes@economiematin.fr
  2. Le texte est en piece jointe (Word, PDF) ou dans le corps du mail

  EXTRACTION INTELLIGENTE (NOUVEAU v3.8) :
  Le systeme utilise l'IA (Claude) pour extraire UNIQUEMENT le texte de la tribune
  depuis le corps de l'email, en supprimant automatiquement :
  - Le message d'introduction ("Voici la tribune de...")
  - Les en-tetes de forward (De:, A:, Date:, Objet:)
  - Les signatures email
  - Les formules de politesse
  - Les liens de tracking

  3. Le systeme automatiquement :
     - Extrait et preserve le texte original (aucune modification)
     - Detecte le site cible dans le corps du mail (fuzzy matching)
     - Genere titre, chapo, intertitres H3
     - Optimise le SEO (tags, categories, meta description)
     - Recherche une image via Periscope (Shutterstock, Wikimedia, etc.)
     - Publie en brouillon sur WordPress
     - Assigne la categorie "Les Experts"
     - Attribue l'auteur detecte dans l'email

Le suivi des tribunes est visible dans la page "Tribunes" du menu lateral.`
  },
  {
    id: 'periscope',
    title: '8. Periscope - Recherche d\'images',
    icon: Image,
    content: `Periscope est un outil de recherche d'images multi-sources :

  Sources disponibles :
  - Wikimedia Commons (gratuit, Creative Commons)
  - Pixabay (gratuit, licence commerciale)
  - Pexels (gratuit, licence commerciale)
  - Flickr (gratuit, flux public)
  - Shutterstock (via scraping, images editoriales)

  Utilisation :
  1. Depuis le menu lateral, cliquez sur "Periscope"
  2. Entrez votre recherche (en francais ou anglais)
  3. Les resultats s'affichent avec preview
  4. Filtrez par format paysage si besoin
  5. Cliquez sur une image pour la telecharger ou l'uploader vers WordPress

  Integration avec la creation d'articles :
  - Choisissez "Shutterstock/Periscope" dans les options d'image
  - 3 images sont proposees automatiquement apres generation
  - Cliquez sur l'image de votre choix pour la selectionner`
  },
  {
    id: 'surveillance',
    title: '9. Outils de Surveillance',
    icon: Monitor,
    content: `Trois outils de veille automatique :

  RADAR (Veille RSS) :
  - Surveille des flux RSS selon vos mots-cles
  - Detecte les articles pertinents automatiquement
  - Peut declencher la generation d'un article

  SONAR (Veille Pages Web) :
  - Surveille le contenu textuel de pages web
  - Deux modes : par intervalle ou a heure fixe quotidienne
  - Detecte les changements de contenu

  CIBLE (Surveillance Publications) :
  - Surveille les nouvelles publications sur une page
  - Detecte l'apparition de nouveaux articles/liens
  - Alerte quand un nouveau contenu est publie

  Configuration depuis le menu lateral (Radar, Sonar, Cible).`
  },
  {
    id: 'couts',
    title: '10. Suivi des Couts',
    icon: BarChart3,
    content: `La page "Couts" affiche en temps reel :
  - Total du mois en cours et du mois precedent
  - Total cumule depuis le debut
  - Repartition par type (texte / images)
  - Repartition par site
  - Historique sur 6 mois

Tarifs indicatifs :
  - Generation de texte : ~0.03 EUR
  - Generation d'image IA : ~0.02 EUR
  - Image Periscope : gratuit
  - Total par article : ~0.03 a 0.05 EUR`
  },
  {
    id: 'wordpress',
    title: '11. Configuration WordPress',
    icon: Settings,
    content: `Pour configurer un site WordPress :

  1. Allez dans "WordPress" dans le menu lateral
  2. Selectionnez le site a configurer
  3. Entrez vos identifiants :
     - Email WordPress
     - Mot de passe d'application (genere dans WordPress > Profil > Mots de passe d'application)
  4. Cliquez sur "Tester la connexion"

Note : L'application gere automatiquement l'authentification JWT ou Basic Auth selon le site.

IMPORTANT : N'utilisez PAS votre mot de passe WordPress habituel. Generez un "mot de passe d'application" dans votre profil WordPress pour plus de securite.`
  },
  {
    id: 'equipage',
    title: '12. Gestion Equipage (RBAC)',
    icon: Shield,
    content: `La page "Equipage" permet de gerer les utilisateurs (Amiral uniquement) :

  Grades :
  - AMIRAL : Acces total, peut creer/modifier/supprimer des utilisateurs
  - CAPITAINE : Acces intermediaire, sites restreints
  - LIEUTENANT : Acces limite aux sites autorises

  Actions :
  - Creer un nouveau membre d'equipage
  - Modifier le grade d'un utilisateur
  - Restreindre l'acces a certains sites
  - Supprimer un compte`
  },
  {
    id: 'tips',
    title: '13. Conseils & Astuces',
    icon: Lightbulb,
    content: `Pour de meilleurs resultats :

  - Soyez PRECIS dans votre sujet (contexte, chiffres, noms)
  - Ajoutez des URLs de reference pour enrichir le contenu
  - Utilisez le prompt personnalise pour des instructions specifiques
  - Imposez un titre si vous en avez un en tete (champ "Titre de l'article")
  - Utilisez "enrichir avec des donnees" dans vos notes pour activer la recherche web
  - Verifiez TOUJOURS le contenu avant publication (editable directement)
  - Le score SEO doit etre >= 60 pour publier (ameliorez focus keyword si trop bas)
  - Pour les tribunes : incluez "Tribune de [Nom]" dans le prompt
  - Pour signer "La Redaction" : ajoutez "signe La Redaction" dans le prompt
  - Le mode "Multi Sites" adapte automatiquement l'angle editorial a chaque site
  - Choisissez Shutterstock/Periscope pour des images professionnelles gratuites
  - Vous pouvez regenerer une image sans regenerer l'article`
  },
  {
    id: 'interviews',
    title: '14. Service Auto-Interview',
    icon: Mail,
    content: `Le service Auto-Interview gere les propositions d'interview par email.

  EMAIL SURVEILLE : interviews@economiematin.fr

  ACTIVATION DU SERVICE (admin) :
  - Activer/desactiver depuis Profile > Services
  - Configurer les emails de notification redaction (pgaroscio, secretaireredaction)

  FORMAT DU TRANSFERT (IMPORTANT) :
  Pour cibler plusieurs sites, ajoutez ces lignes EN TETE du mail transfere :

  SITES: economiematin, socialmag
  CANONICAL: economiematin

  - SITES : liste des sites sur lesquels publier (separes par virgule)
  - CANONICAL : le site de reference pour le SEO (evite le duplicate content)
  - Si aucune ligne n'est indiquee : fallback sur economiematin uniquement

  WORKFLOW AUTOMATIQUE :
  1. Reception d'une proposition sur interviews@
  2. Analyse automatique du sujet (detection de l'interviewe, de l'entreprise, des angles)
  3. Generation de questions adaptees a EconomieMatin
  4. Envoi automatique des questions a l'expediteur original (PAS au forwarder)
  5. Relances automatiques (max 5 fois, espacees de 24-48h)
  6. Abandon automatique au-dela de 5 relances sans reponse
  7. A la reception des reponses : generation d'un article narratif
  8. Publication en BROUILLON sur WordPress (pending)
  9. Notification aux editeurs avec lien d'edition

  MULTI-SITES + CANONICAL :
  - L'article est publie d'abord sur le site canonical (sans rel=canonical)
  - Puis duplique sur les autres sites avec un encart et un tag canonical
  - Protege votre SEO contre le duplicate content Google

  DETECTION AUTO-PROMOTIONNELLE :
  - L'IA ne rejette PLUS les propositions "trop commerciales"
  - Les passages auto-promotionnels sont SURLIGNES EN ROUGE dans le brouillon
  - Un encart jaune en tete liste les passages a revoir avant publication

  GESTION DES INTERVIEWS REJETEES :
  - Page Interviews > filtre "A retraiter" (combine redirected + abandoned)
  - Bouton "Retraiter comme approuvee" sur chaque entree
    - Si reponses deja recues : relance la generation d'article
    - Sinon : ressaie l'envoi des questions (compteur relances remis a zero)

  SECURITE (GARDE-FOUS) :
  - Impossible d'envoyer une relance a un domaine interne (@economiematin.fr)
  - Impossible d'envoyer une relance a la boite interviews@ elle-meme
  - Impossible d'envoyer une relance au forwarder (par defaut: le redacteur)
  - En cas d'echec d'extraction de l'adresse : l'interview passe en "needs_manual_review"
    et un email [INTERVIEW A CORRIGER] est envoye aux editeurs`
  },
  {
    id: 'partenaires',
    title: '15. Service Partenaires',
    icon: Settings,
    content: `Le service Partenaires gere les demandes de publication par email :

  Email surveille : partenaires@economiematin.fr

  TYPES DE CONTENUS ACCEPTES :

  1. ARTICLE PRET A PUBLIER
     Si le partenaire envoie un article complet (pas marque "communique de presse" 
     ou "dossier de presse"), il est injecte tel quel dans le CMS.

  2. COMMUNIQUE / DOSSIER DE PRESSE
     Si le contenu est marque "communique de presse" ou "dossier de presse",
     l'IA genere un article journalistique a partir du document.

  3. LISTE D'ARTICLES EN PIECE JOINTE
     Si le partenaire envoie plusieurs articles avec le site de destination 
     mentionne dans chaque article, ils sont automatiquement places sur les 
     bons sites.

  WORKFLOW :

  Cas 1 - Consignes completes dans l'email :
  Si le partenaire indique le site et la date/heure dans son email ou ses 
  pieces jointes, le traitement est automatique (pas de formulaire).

  Cas 2 - Sans consignes :
  1. Reception de l'email
  2. Envoi automatique d'un formulaire (selection sites + date)
  3. Le partenaire repond avec ses choix
  4. Articles generes/injectes en BROUILLON
  5. Compte-rendu avec liens de previsualisation

  CORRECTIONS POST-PUBLICATION :

  Corrections MINEURES (automatiques) :
  - Faute sur un nom propre
  - Mot manquant
  - Phrase courte a remplacer

  Corrections MAJEURES (refusees) :
  - Reecriture de paragraphes
  - Changement de l'angle editorial
  → Un email est envoye : "Je ne peux pas executer cette correction 
     sans intervention d'un journaliste de la redaction"

  REGLES :
  - CC obligatoire : Paolo et JB sur tous les echanges
  - Whitelist : economiematin.fr + jbgiraud@mac.com (super utilisateur)
  - Prefixe titre : "PARTENAIRE A VALIDER NE PAS PUBLIER - "
  - Les articles ne sont JAMAIS publies automatiquement`
  },
  {
    id: 'sites-non-connectes',
    title: '16. Sites Non Connectes',
    icon: Settings,
    content: `Certains sites peuvent etre ajoutes meme sans connexion WordPress fonctionnelle :

  Pourquoi ?
  - Certains CMS ne supportent pas l'API REST WordPress
  - La connexion peut echouer temporairement (firewall, maintenance)
  - Vous voulez preparer la configuration avant d'avoir les acces

  Comment ca marche :
  - Le site est marque comme "NON CONNECTE" (badge orange)
  - Il apparait dans le Dashboard avec une animation orange
  - Vous pouvez creer des articles pour ce site (copier/coller manuel)
  - La publication automatique est desactivee

  Dans le Centre de Commande :
  - Sites ACTIFS (blanc) : Connectes et operationnels
  - Sites NON CONNECTES (orange) : Configures mais sans publication auto
  - Sites NON CONFIGURES (rouge) : Pas encore dans la base
  - Sites VERROUILLES (gris) : Reserves pour le futur`
  },
  {
    id: 'stockage-cloud',
    title: '17. Stockage Cloud',
    icon: Settings,
    content: `Auto-Edito utilise un stockage cloud persistant :

  Avantages :
  - Vos fichiers (documents, images, audio) persistent apres redemarrage
  - Acces partage entre tous les utilisateurs
  - Espace de stockage illimite

  Fichiers concernes :
  - Documents uploades (PDF, DOCX, ODT, TXT)
  - Images generees par l'IA
  - Enregistrements audio (dictee)
  - Livres de reference

  Note : Les anciens fichiers stockes localement restent accessibles grace a un systeme de fallback automatique.`
  }
];

export default UserGuide;
